import React, { useEffect, useState, useMemo } from 'react';
import { Typography, Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress } from '@mui/material';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import UpdatePassword from './updatePasswordProfile.js';
import UpdateProfile from './updateProfile';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { resetGlobalState, persistor } from '../../store.js';
import axios from 'axios';
import { supabase, supabaseAnonKey, supabaseUrl } from '../../supabaseClient.js';


const ManageAccountProfile = ({initialOption, showWelcome}) => {
    const outerTheme = useTheme();
    const profileTheme = useMemo(() => createTheme(outerTheme, {
        palette: { primary: { main: '#29C0BB', dark: '#1da8a3', contrastText: '#FFFFFF' } },
        components: { MuiButton: { styleOverrides: { root: { textTransform: 'none', borderRadius: 8 } } } },
    }), [outerTheme]);
    const [optionSelected, setOptionSelected] = useState(initialOption === 'Reset password' ? initialOption : '');
    const [accountOpen, setAccountOpen] = useState(initialOption === 'Reset password');
    useEffect(() => { if (initialOption === 'Reset password') { setOptionSelected(initialOption); setAccountOpen(true); } }, [initialOption]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteStep, setDeleteStep] = useState('preview');
    const [deletePreview, setDeletePreview] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [deleteConfirmation, setDeleteConfirmation] = useState('');
    const { userDetails } = useSelector((state) => ({userDetails: state.globalState.userDetails}), shallowEqual);
    const dispatch = useDispatch();
    const navigate = useNavigate();


    const getDeleteHeaders = async () => {
        const { data } = await supabase.auth.getSession();
        const accessToken = data?.session?.access_token;
        if (!accessToken) throw new Error('Your session has expired. Please sign in again.');
        return {
            'Authorization': `Bearer ${accessToken}`,
            'apikey': supabaseAnonKey,
            'Content-Type': 'application/json',
        };
    };

    const openDeleteCheck = async () => {
        setDeleteDialogOpen(true);
        setDeleteStep('preview');
        setDeletePreview(null);
        setDeleteError('');
        setDeleteConfirmation('');
        setDeleteLoading(true);
        try {
            const response = await axios.post(
                `${supabaseUrl}/functions/v1/delete-scribe-user`,
                { action: 'preview' },
                { headers: await getDeleteHeaders() }
            );
            setDeletePreview(response.data.preview);
        } catch (error) {
            console.error('Error checking account deletion requirements:', error);
            setDeleteError(error.response?.data?.error || error.message || 'Unable to check account deletion requirements.');
        } finally {
            setDeleteLoading(false);
        }
    };

    const deleteUser = async () => {
        setDeleteLoading(true);
        setDeleteError('');
        try {
            await axios.post(
                `${supabaseUrl}/functions/v1/delete-scribe-user`,
                { confirmClinicalRecordDeletion: true },
                { headers: await getDeleteHeaders() }
            );
            await supabase.auth.signOut();
            dispatch(resetGlobalState());
            await persistor.purge();
            navigate('/login');
        } catch (error) {
            console.error('Error deleting user:', error);
            if (error.response?.status === 409 && error.response?.data?.preview) {
                setDeletePreview(error.response.data.preview);
                setDeleteStep('preview');
            }
            setDeleteError(error.response?.data?.error || 'An error occurred while deleting the account.');
        } finally {
            setDeleteLoading(false);
        }
    };

    const blockers = deletePreview ? [
        deletePreview.activeConnections > 0 && `${deletePreview.activeConnections} active Client Connection(s). Cancel every Client Connection before deleting your account.`,
        deletePreview.activeSubscriptions > 0 && `${deletePreview.activeSubscriptions} active Stripe subscription(s). Cancel the subscription before deleting your account.`,
        deletePreview.futureAppointments > 0 && `${deletePreview.futureAppointments} upcoming appointment(s). Resolve or cancel every future appointment before deleting your account.`,
        deletePreview.unpaidPayoutAppointments > 0 && `${deletePreview.unpaidPayoutAppointments} paid appointment(s) awaiting payout. Deleting now could cause the platform to lose payout tracking, so deletion is blocked until payout is complete.`,
    ].filter(Boolean) : [];

    return (
        <ThemeProvider theme={profileTheme}><div>
            <UpdateProfile userid={userDetails.id} useremail={userDetails.email} username={userDetails.name} userpracticetype={userDetails.practicetype} data_retention_days={userDetails.data_retention_days} showWelcome={showWelcome} onOpenAccountSettings={() => { setOptionSelected(''); setAccountOpen(true); }} />
            <Dialog open={accountOpen && !deleteDialogOpen} onClose={() => setAccountOpen(false)} maxWidth="sm" fullWidth aria-labelledby="account-settings-title" PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle id="account-settings-title">Account settings</DialogTitle>
                <DialogContent>
                    {optionSelected === 'Reset password' ? <UpdatePassword setOptionSelected={setOptionSelected} /> : <>
                        <Typography color="text.secondary" sx={{ mb: 2 }}>Manage your password and account.</Typography>
                        <Button fullWidth variant="outlined" onClick={() => setOptionSelected('Reset password')} sx={{ justifyContent: 'space-between', p: 2, textTransform: 'none' }}>Reset password <KeyboardArrowRightIcon /></Button>
                        <Divider sx={{ my: 3 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>We will check Client Connections, appointments, payouts and subscriptions before permanent deletion.</Typography>
                        <Button color="error" onClick={openDeleteCheck}>Delete Account</Button>
                    </>}
                </DialogContent>
                <DialogActions><Button onClick={() => setAccountOpen(false)}>Close</Button></DialogActions>
            </Dialog>
            <Dialog open={deleteDialogOpen} onClose={() => !deleteLoading && setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{deleteStep === 'preview' ? 'Before you delete your account' : 'Delete clinical records permanently?'}</DialogTitle>
                <DialogContent>
                    {deleteLoading && !deletePreview ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
                    ) : deleteStep === 'preview' ? (
                        <>
                            {deleteError && <Typography color="error" sx={{ mb: 2 }}>{deleteError}</Typography>}
                            {blockers.length > 0 ? (
                                <>
                                    <Typography sx={{ mb: 1.5 }}>Your account cannot be deleted yet. Please resolve the following first:</Typography>
                                    <Box component="ul" sx={{ pl: 3, mt: 0 }}>
                                        {blockers.map((blocker) => <li key={blocker}><Typography sx={{ mb: 1 }}>{blocker}</Typography></li>)}
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">These checks protect patient bookings and prevent the platform from losing payout or connection tracking.</Typography>
                                </>
                            ) : deletePreview ? (
                                <>
                                    <Typography sx={{ mb: 1.5 }}>There are no active Client Connections, paid appointments awaiting payout, future appointments, or active Stripe subscriptions blocking deletion.</Typography>
                                    <Typography color="error">The next step permanently deletes your TikCare-hosted patient records, clinical reports, transcripts and uploaded attachments where applicable. This cannot be undone.</Typography>
                                </>
                            ) : null}
                        </>
                    ) : (
                        <>
                            <Typography sx={{ mb: 2 }}>Do you want to permanently delete the patient records, reports, transcripts and uploaded attachments associated with this account?</Typography>
                            <Typography color="error" sx={{ mb: 2 }}>Type DELETE to confirm. Choosing Cancel keeps your account and records unchanged.</Typography>
                            <TextField autoFocus fullWidth label="Type DELETE" value={deleteConfirmation} onChange={(event) => setDeleteConfirmation(event.target.value)} />
                            {deleteError && <Typography color="error" sx={{ mt: 2 }}>{deleteError}</Typography>}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>Cancel</Button>
                    {deleteStep === 'preview' && blockers.length === 0 && deletePreview && (
                        <Button variant="contained" color="error" onClick={() => { setDeleteStep('confirm'); setDeleteError(''); }}>Continue</Button>
                    )}
                    {deleteStep === 'confirm' && (
                        <Button variant="contained" color="error" disabled={deleteConfirmation !== 'DELETE' || deleteLoading} onClick={deleteUser}>
                            {deleteLoading ? 'Deleting…' : 'Permanently delete account and records'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </div></ThemeProvider>
    );
  };

export default ManageAccountProfile;
